import { useCallback, type KeyboardEvent, type ChangeEvent, type ClipboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Textarea } from '@scio3/ui';
import { SendHorizonal, Plus, Square } from 'lucide-react';
import { MAX_INPUT_LENGTH } from '../utils/constants';
import { isEnterKey } from '@/lib/keyboard';
import { ImagePreviewBar } from './ImagePreviewBar';
import type { UploadedImage } from '../hooks/useImageUpload';

interface OptiBotInputProps {
  input: string;
  setInput: (value: string) => void;
  onSend: (input: string) => void;
  isLoading: boolean;
  onStop: () => void;
  /** Image upload state */
  images: UploadedImage[];
  isUploading: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  acceptedExtensions: string;
  onOpenFilePicker: () => void;
  onFileSelect: (files: FileList | null) => void;
  onRemoveImage: (localId: string) => void;
}

/**
 * Chat input with auto-resize textarea, (+) image attach button, and send button.
 * Enter sends the message; Shift+Enter inserts a newline.
 */
export function OptiBotInput({
  input,
  setInput,
  onSend,
  isLoading,
  onStop,
  images,
  isUploading,
  fileInputRef,
  acceptedExtensions,
  onOpenFilePicker,
  onFileSelect,
  onRemoveImage,
}: OptiBotInputProps) {
  const { t } = useTranslation();

  const canSend = (input.trim().length > 0 || images.length > 0) && !isLoading && !isUploading;

  const handleSend = useCallback(() => {
    if (!canSend) return;
    onSend(input.trim());
    setInput('');
  }, [canSend, input, onSend, setInput]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (isEnterKey(e) && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      if (value.length <= MAX_INPUT_LENGTH) {
        setInput(value);
      }
    },
    [setInput],
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLTextAreaElement>) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const imageFiles: File[] = [];
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) imageFiles.push(file);
        }
      }

      if (imageFiles.length === 0) return;

      e.preventDefault();
      const dt = new DataTransfer();
      imageFiles.forEach((f) => dt.items.add(f));
      onFileSelect(dt.files);
    },
    [onFileSelect],
  );

  return (
    <div className="shrink-0 border-t border-border px-2 py-3">
      <ImagePreviewBar images={images} onRemove={onRemoveImage} />
      <div className="flex items-end gap-2">
        <Button
          variant="ghost"
          size="icon-lg"
          onClick={onOpenFilePicker}
          disabled={isLoading || images.length >= 6}
          aria-label={t('optibot.image.attach')}
          className="shrink-0"
        >
          <Plus className="h-4 w-4" />
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedExtensions}
          multiple
          className="hidden"
          onChange={(e) => onFileSelect(e.target.files)}
        />
        <Textarea
          value={input}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={t('optibot.placeholder')}
          maxLength={MAX_INPUT_LENGTH}
          rows={1}
          className="min-h-[40px] max-h-[120px] resize-none"
        />
        {isLoading ? (
          <Button
            variant="ghost"
            size="icon-lg"
            className="shrink-0 self-end rounded-md text-muted-foreground hover:text-foreground"
            onClick={onStop}
            aria-label={t('optibot.stop')}
          >
            <Square className="h-3 w-3 rounded-[1px] fill-current" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon-lg"
            className="shrink-0 self-end rounded-md text-primary hover:text-primary/80"
            onClick={handleSend}
            disabled={!canSend}
            aria-label={t('optibot.send')}
          >
            <SendHorizonal className="h-5 w-5 fill-current" />
          </Button>
        )}
      </div>
    </div>
  );
}
